import { cloneDeep } from 'lodash-es'
import { nanoid } from 'nanoid/non-secure'
import type { IUpdateOptions } from '@/plugins/cad/module/CADModule/object/objectManager/BaseObjectManager'
import { Command } from '@/plugins/cad/module/core/Command'
import { getLoggerManager } from '@/shared/logging'
import type { CadLayerCreateInput, CadLayerUpdatePatch } from '@/shared/types/services'
import type { CADViewManager } from '../manager/CADViewManager'
import type { IEntity } from '../model/entity'
import { type ILayer, Layer } from '../model/layer/Layer'

const MATERIAL_UPDATE_OPTIONS: IUpdateOptions = {
  updateGeometry: false,
  updateMaterial: true,
}

const LAYER_STYLE_KEYS = ['colorMethod', 'color', 'lineType', 'lineWidth'] as const

const log = getLoggerManager().getLogger('cad')

function hasOwn<T extends object>(target: T, key: PropertyKey): boolean {
  return Object.hasOwn(target, key)
}

function cloneLayerState(layer: Layer): ILayer {
  return cloneDeep(layer.serialize())
}

/** 新建图层命令 */
export class CreateLayerCommand extends Command {
  private layers: Layer[] = []

  constructor(
    private input: CadLayerCreateInput | CadLayerCreateInput[],
    private viewManager: CADViewManager
  ) {
    super()
  }

  /** 根据输入创建图层并加入数据管理器 */
  execute(): void {
    const isBatch = Array.isArray(this.input)
    const inputArray = isBatch
      ? (this.input as CadLayerCreateInput[])
      : [this.input as CadLayerCreateInput]
    this.layers = inputArray.map(
      (input) =>
        new Layer({
          ...cloneDeep(input),
          id: input.id ?? nanoid(),
        })
    )

    for (const layer of this.layers) {
      const existingLayer = this.viewManager.dataManager.getLayer(layer.id)
      if (existingLayer && existingLayer !== layer) {
        if (!isBatch) {
          throw new Error(`图层已存在: ${layer.id}`)
        }
        log.warn(`图层已存在: ${layer.id}`)
        continue
      }
      this.viewManager.dataManager.addLayer(layer)
    }
  }

  /** 撤销：移除本次创建的所有图层 */
  undo(): void {
    for (const layer of this.layers) {
      this.viewManager.dataManager.deleteLayer(layer.id)
    }
  }

  /**
   * 获取本次命令创建的图层集合。
   *
   * @returns 已创建的图层数组，顺序与输入一致
   */
  getLayers(): Layer[] {
    return this.layers
  }
}

/** 删除图层命令 */
export class RemoveLayerCommand extends Command {
  private layer: Layer | undefined
  private layerEntities: IEntity[] = []

  constructor(
    private layerId: string,
    private viewManager: CADViewManager
  ) {
    super()
  }

  /** 删除图层及其包含的所有实体 */
  execute(): void {
    const layer = this.viewManager.dataManager.getLayer(this.layerId)
    if (!layer) {
      return
    }

    this.layer = layer
    this.layerEntities = [...this.viewManager.getEntitiesByLayerId(this.layerId)]

    if (this.layerEntities.length > 0) {
      this.viewManager.deleteEntities(this.layerEntities)
    }
    this.viewManager.dataManager.deleteLayer(this.layerId)
  }

  /** 撤销：恢复被删除的图层及其实体 */
  undo(): void {
    if (!this.layer) {
      return
    }

    this.viewManager.dataManager.addLayer(this.layer)
    if (this.layerEntities.length > 0) {
      this.viewManager.addEntities(this.layerEntities)
    }
  }

  /**
   * 获取被删除的图层 id。
   *
   * @returns 目标图层 id
   */
  getLayerId(): string {
    return this.layerId
  }
}

/** 更新图层命令 */
export class UpdateLayerCommand extends Command {
  private readonly previousEntityStates = new Map<string, IEntity>()
  private previousLayerState: ILayer | undefined

  constructor(
    private layerId: string,
    private patch: CadLayerUpdatePatch,
    private viewManager: CADViewManager
  ) {
    super()
  }

  /** 应用 patch 更新图层属性，必要时同步更新图层内实体样式 */
  execute(): void {
    const layer = this.viewManager.dataManager.getLayer(this.layerId)
    if (!layer) {
      return
    }

    if (!this.previousLayerState) {
      this.previousLayerState = cloneLayerState(layer)
    }

    const layerEntities = this.viewManager.getEntitiesByLayerId(this.layerId)
    const shouldUpdateEntityStyle = this.shouldUpdateEntityStyle()
    if (shouldUpdateEntityStyle && this.previousEntityStates.size === 0) {
      for (const entity of layerEntities) {
        this.previousEntityStates.set(entity.id, cloneDeep(entity))
      }
    }

    const previousVisible = layer.visible
    Object.assign(layer, cloneDeep(this.patch))

    if (shouldUpdateEntityStyle) {
      const entityPatch = this.getEntityStylePatch()
      for (const entity of layerEntities) {
        Object.assign(entity, cloneDeep(entityPatch))
      }
      this.viewManager.updateEntities(layerEntities, MATERIAL_UPDATE_OPTIONS)
    }

    if (previousVisible !== layer.visible) {
      this.viewManager.objectManager.setEntitiesVisible(layerEntities, layer.visible)
    }
  }

  /** 撤销：恢复图层及其实体在执行前的状态 */
  undo(): void {
    const layer = this.viewManager.dataManager.getLayer(this.layerId)
    if (!layer || !this.previousLayerState) {
      return
    }

    const previousVisible = layer.visible
    Object.assign(layer, cloneDeep(this.previousLayerState))

    const layerEntities = this.viewManager.getEntitiesByLayerId(this.layerId)
    if (this.previousEntityStates.size > 0) {
      for (const entity of layerEntities) {
        const previousState = this.previousEntityStates.get(entity.id)
        if (!previousState) {
          continue
        }
        Object.assign(entity, cloneDeep(previousState))
      }
      this.viewManager.updateEntities(layerEntities, MATERIAL_UPDATE_OPTIONS)
    }

    if (previousVisible !== layer.visible) {
      this.viewManager.objectManager.setEntitiesVisible(layerEntities, layer.visible)
    }
  }

  private shouldUpdateEntityStyle(): boolean {
    return LAYER_STYLE_KEYS.some((key) => hasOwn(this.patch, key))
  }

  private getEntityStylePatch(): Partial<IEntity> {
    const entityPatch: Partial<IEntity> = {}
    if (hasOwn(this.patch, 'colorMethod')) {
      entityPatch.colorMethod = cloneDeep(this.patch.colorMethod) as IEntity['colorMethod']
    }
    if (hasOwn(this.patch, 'color')) {
      entityPatch.color = cloneDeep(this.patch.color) as IEntity['color']
    }
    if (hasOwn(this.patch, 'lineType')) {
      entityPatch.lineType = cloneDeep(this.patch.lineType) as IEntity['lineType']
    }
    if (hasOwn(this.patch, 'lineWidth')) {
      entityPatch.lineWidth = cloneDeep(this.patch.lineWidth) as IEntity['lineWidth']
    }
    return entityPatch
  }
}
